import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Chip, 
  Divider, 
  Grid, 
  IconButton, 
  Paper, 
  Tab,
  Tabs,
  Typography,
  styled,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  DoNotDisturb as NoShowIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import athenaService from '../../../services/athenaService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, addDays, isValid } from 'date-fns';

// Custom styled components
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  marginRight: theme.spacing(1),
  minWidth: 0,
  padding: theme.spacing(1, 2),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

const DetailContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
  marginBottom: theme.spacing(0.5),
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  marginBottom: theme.spacing(2),
}));

const AppointmentDetails = ({ appointment, onClose, initialDepartments = [], initialProviders = [] }) => {
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [cancelReasons, setCancelReasons] = useState([]);
  const [selectedCancelReasonId, setSelectedCancelReasonId] = useState('');
  const [customCancelReason, setCustomCancelReason] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [loadingReasons, setLoadingReasons] = useState(false);
  
  // Checkout and checkin states
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  
  // Reschedule states
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // Loading states for confirmation actions
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [submittingReschedule, setSubmittingReschedule] = useState(false);
  
  // Log the actual appointment data received and use departments and providers from props
  useEffect(() => {
    console.log("AppointmentDetails received data:", appointment);
    
    // Use departments and providers passed as props
    if (initialDepartments && initialDepartments.length > 0) {
      setDepartments(initialDepartments);
      setLoadingDepartments(false);
    }
    
    if (initialProviders && initialProviders.length > 0) {
      setProviders(initialProviders);
      setLoadingProviders(false);
    }
  }, [initialDepartments, initialProviders, appointment]);

  // Fetch cancel reasons when dialog is opened
  const fetchCancelReasons = async () => {
    setLoadingReasons(true);
    try {
      const response = await athenaService.getAppointmentCancelReasons();
      console.log('Cancel reasons fetched in AppointmentDetails:', response);
      
      // Check for the correct property structure from the API
      if (response && response.appointmentcancelreasons && Array.isArray(response.appointmentcancelreasons)) {
        const reasonsArray = response.appointmentcancelreasons;
        setCancelReasons(reasonsArray);
        
        // Set default selected reason if available
        if (reasonsArray.length > 0) {
          setSelectedCancelReasonId(reasonsArray[0].appointmentcancelreasonid.toString());
        }
      } else {
        console.warn('Response does not contain appointmentcancelreasons array:', response);
        setCancelReasons([]);
      }
    } catch (error) {
      console.error('Error fetching cancel reasons:', error);
      setCancelReasons([]);
      
      // Extract detailed error message if available
      let errorMessage = 'Failed to load cancellation reasons';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show error in snackbar
      setSnackbar({
        open: true,
        message: `${errorMessage}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    fetchCancelReasons();
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setSelectedCancelReasonId('');
    setCustomCancelReason('');
  };

  const handleCancelReasonChange = (event) => {
    setSelectedCancelReasonId(event.target.value);
  };

  const handleCustomReasonChange = (event) => {
    setCustomCancelReason(event.target.value);
  };

  const handleCancelAppointment = async () => {
    // Set loading state
    setSubmittingCancel(true);
    
    try {
      // Find the reason text for selected reason ID
      const selectedReason = cancelReasons.find(reason => reason.appointmentcancelreasonid.toString() === selectedCancelReasonId);
      
      const response = await athenaService.cancelAppointment({
        appointmentId: appointment.appointmentid || appointment.id,
        patientId: appointment.patientid || appointment.patient?.patientid || appointment.patient?.id,
        appointmentCancelReasonId: parseInt(selectedCancelReasonId, 10),
        cancellationReason: customCancelReason || (selectedReason ? selectedReason.name : 'Cancelled by user'),
        ignoreSchedulablePermission: true
      });
      
      console.log('Appointment cancelled:', response);
      
      // Close the dialog
      handleCloseCancelDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Appointment cancelled successfully',
        severity: 'success'
      });
      
      // Close the details view after a brief delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not cancel the appointment';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show error message
      setSnackbar({
        open: true,
        message: `Appointment cancellation failed: ${errorMessage}`,
        severity: 'error'
      });
      
      // Close the dialog
      handleCloseCancelDialog();
    } finally {
      // Reset loading state
      setSubmittingCancel(false);
    }
  };
  
  const handleOpenRescheduleDialog = () => {
    setRescheduleDialogOpen(true);
    setSelectedDate(new Date());
    setSelectedLocation('');
    
    // Load location/department data
    loadRescheduleData();
    
    // Load cancellation reasons
    fetchCancelReasons();
    
    // Log for debugging
    console.log('Opening reschedule dialog - loading cancellation reasons');
  };

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setSelectedSlotId('');
    setRescheduleReason('');
    setSelectedDepartment('');
    setSelectedProvider('');
    setSelectedLocation('');
  };

  const loadRescheduleData = async () => {
    try {
      setLoadingDepartments(true);
      const deps = await athenaService.getDepartments();
      
      // Helper function to normalize city names for consistent formatting
      const toCamelCase = (str) => {
        if (!str) return '';
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };
      
      // Filter out departments with empty providerlist
      const filteredDepts = deps.filter(dept => 
        dept.providerlist && dept.providerlist.length > 0
      );
      
      setDepartments(filteredDepts || []);
      
      // Group departments by location (city) - like in AppointmentScheduler
      const deptsByLocation = {};
      const locationList = [];
      
      filteredDepts.forEach(dept => {
        const locationName = toCamelCase(dept.city) || 'Unknown';
        
        // Initialize location group if it doesn't exist
        if (!deptsByLocation[locationName]) {
          deptsByLocation[locationName] = [];
          locationList.push({
            name: locationName,
            departments: []
          });
        }
        
        // Add department to its location group
        deptsByLocation[locationName].push(dept);
        
        // Update the location list with this department
        const locationIndex = locationList.findIndex(loc => loc.name === locationName);
        if (locationIndex >= 0) {
          locationList[locationIndex].departments.push(dept);
        }
      });
      
      console.log("Departments grouped by location:", deptsByLocation);
      console.log("Location list:", locationList);
      
      setLocations(locationList);
      setDepartmentsByLocation(deptsByLocation);
      
      // Set the initial department to the appointment's department
      if (appointment.departmentid) {
        setSelectedDepartment(appointment.departmentid.toString());
        
        // Figure out which location this department belongs to
        const deptCity = filteredDepts.find(d => d.departmentid.toString() === appointment.departmentid.toString())?.city;
        if (deptCity) {
          const locationName = toCamelCase(deptCity);
          setSelectedLocation(locationName);
        }
        
        // Also load providers for this department
        setLoadingProviders(true);
        const provs = await athenaService.getProviders(parseInt(appointment.departmentid));
        setProviders(provs || []);
      }
      
      // If the appointment has a provider, select it by default
      if (appointment.providerid) {
        setSelectedProvider(appointment.providerid.toString());
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleLocationChange = async (event) => {
    const location = event.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    setSelectedDepartment('');
    setSelectedProvider('');
    
    if (!location) return;
    
    try {
      setLoadingProviders(true);
      
      // Get all departments for this location
      const locationDepartments = departmentsByLocation[location] || [];
      console.log(`Found ${locationDepartments.length} departments for location ${location}`);
      
      if (locationDepartments.length === 0) {
        setProviders([]);
        setLoadingProviders(false);
        return;
      }
      
      // Select the first department from this location
      const firstDepartment = locationDepartments[0];
      setSelectedDepartment(firstDepartment.departmentid.toString());
      
      // To combine all providers across departments
      const allProviders = [];
      // Set to track unique provider IDs
      const allProviderIds = new Set();
      
      // Loop through all departments in this location
      for (const dept of locationDepartments) {
        // Load providers for each department
        const departmentProviders = await athenaService.getProviders(parseInt(dept.departmentid));
        
        // Filter and add to all providers collection
        for (const provider of departmentProviders) {
          // Only add if not already in the collection
          if (!allProviderIds.has(provider.providerid)) {
            allProviderIds.add(provider.providerid);
            allProviders.push(provider);
          }
        }
      }
      
      console.log(`Found ${allProviders.length} providers across all departments in location ${location}`);
      setProviders(allProviders || []);
      
      // If this is a rescheduling of an existing appointment, try to keep the same provider
      if (appointment.providerid) {
        const providerExists = allProviders.some(p => p.providerid.toString() === appointment.providerid.toString());
        if (providerExists) {
          setSelectedProvider(appointment.providerid.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching providers for location:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Failed to fetch providers for this location';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };

  const handleDepartmentChange = async (event) => {
    const departmentId = event.target.value;
    setSelectedDepartment(departmentId);
    
    if (!departmentId) {
      setSelectedProvider('');
      return;
    }
    
    try {
      setLoadingProviders(true);
      const provs = await athenaService.getProviders(parseInt(departmentId));
      setProviders(provs || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch providers',
        severity: 'error'
      });
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleDateChange = (newDate) => {
    if (isValid(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDepartment) {
      setSnackbar({
        open: true,
        message: 'Please select a department first',
        severity: 'warning'
      });
      return;
    }
    
    setLoadingSlots(true);
    try {
      // Format date for API
      const formattedDate = format(selectedDate, 'MM/dd/yyyy');
      const endDateFormatted = format(addDays(selectedDate, 1), 'MM/dd/yyyy');
      
      // Log all appointment properties related to appointment type for debugging
      console.log('Appointment object:', {
        appointmentid: appointment.appointmentid,
        appointmenttypeid: appointment.appointmenttypeid,
        appointmenttype: appointment.appointmenttype,
        appointmenttypename: appointment.appointmenttypename,
        fullAppointment: appointment
      });
      
      // Ensure all parameters are properly typed for the API call
      const departmentId = selectedDepartment ? parseInt(selectedDepartment, 10) : null;
      const providerId = selectedProvider ? parseInt(selectedProvider, 10) : null;
      
      // Create appointmentType object structure as expected by athenaService
      const appointmentType = appointment.appointmenttypeid ? {
        appointmenttypeid: parseInt(appointment.appointmenttypeid, 10),
        name: appointment.appointmenttype || appointment.appointmenttypename || 'OFFICE VISIT' // Use name if available or default
      } : null;
      
      // Log the constructed appointmentType object
      console.log('Constructed appointmentType object:', appointmentType);
      
      console.log('Fetching slots with:', {
        departmentId,
        appointmentType,
        providerId,
        startDate: formattedDate,
        endDate: endDateFormatted
      });
      
      // Fetch available appointment slots
      const slots = await athenaService.getAvailableSlots(
        departmentId,
        appointmentType, // Pass as object with {appointmenttypeid, name} structure
        providerId,
        formattedDate,
        endDateFormatted
      );
      
      console.log('Available slots:', slots);
      setAvailableSlots(slots || []);
      
      // Select first slot by default if available
      if (slots && slots.length > 0) {
        setSelectedSlotId(slots[0].appointmentid || slots[0].id);
      } else {
        setSelectedSlotId('');
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load available appointment slots';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Failed to load available slots: ${errorMessage}`,
        severity: 'error'
      });
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSlotChange = (event) => {
    setSelectedSlotId(event.target.value);
  };

  const handleRescheduleReasonChange = (event) => {
    setRescheduleReason(event.target.value);
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedSlotId) {
      setSnackbar({
        open: true,
        message: 'Please select an available appointment slot',
        severity: 'warning'
      });
      return;
    }
    
    // Set loading state
    setSubmittingReschedule(true);
    
    try {
      // Find the reason text for selected reason ID
      const selectedReason = cancelReasons.find(reason => reason.appointmentcancelreasonid.toString() === selectedCancelReasonId);
      
      // Ensure we have all required integer fields
      const appointmentId = appointment.appointmentid || appointment.id;
      const patientId = appointment.patientid || appointment.patient?.patientid || appointment.patient?.id;
      const cancelReasonId = selectedCancelReasonId ? parseInt(selectedCancelReasonId, 10) : 1; // Default to 1 if undefined

      console.log('Sending reschedule data:', {
        appointmentId,
        newAppointmentId: selectedSlotId,
        patientId,
        appointmentCancelReasonId: cancelReasonId,
        rescheduleReason: rescheduleReason || 'Patient requested reschedule'
      });

      const response = await athenaService.rescheduleAppointment({
        appointmentId,
        newAppointmentId: selectedSlotId,
        patientId,
        appointmentCancelReasonId: cancelReasonId,
        rescheduleReason: rescheduleReason || 'Patient requested reschedule',
        ignoreschedulablepermission: true
      });
      
      console.log('Appointment rescheduled:', response);
      
      // Close the dialog
      handleCloseRescheduleDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Appointment rescheduled successfully',
        severity: 'success'
      });
      
      // Close the details view after a brief delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not reschedule the appointment';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Show error message
      setSnackbar({
        open: true,
        message: `Appointment rescheduling failed: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      // Reset loading state
      setSubmittingReschedule(false);
    }
  };
  
  const handleRescheduleAppointment_old = () => {
    // This would typically open a dialog to select a new appointment slot
    // For now, just show an alert that this feature is coming soon
    alert('Reschedule functionality coming soon. This would open a dialog to select a new appointment slot.');
    
    // In a real implementation, we would:
    // 1. Open a dialog to select a new appointment slot
    // 2. Call athenaService.rescheduleAppointment with the old and new appointment IDs
  };

  // Helper function to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not available';
    
    try {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[month-1]} ${day}, ${year}`;
      }
      
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      }
      
      return dateStr;
    } catch (error) {
      return dateStr;
    }
  };

  // Calculate age from DOB
  const calculateAge = (dobStr) => {
    if (!dobStr) return '';
    
    try {
      const parts = dobStr.split('/');
      if (parts.length === 3) {
        const birthDate = new Date(parts[2], parts[0] - 1, parts[1]);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return `(age ${age})`;
      }
      
      const birthDate = new Date(dobStr);
      if (!isNaN(birthDate.getTime())) {
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return `(age ${age})`;
      }
      
      return '';
    } catch (error) {
      return '';
    }
  };

  // Insurance package ID to name mapping
  const getInsuranceProviderName = (packageId) => {
    // Convert to number for comparison
    const id = Number(packageId);
    
    // Map of insurance package IDs to provider names
    const insuranceMap = {
      98546: "Medicare",
      15310: "Blue Cross",
      74: "Cigna",
      2150: "Blue Shield",
      23344: "Aetna",
      660279: "Health Net",
      4953: "United Healthcare",
      655450: "Tricare",
      581754: "Adventist Health",
      19961: "USC Health Plan",
      190449: "Optum Medical Group"
    };
    
    return insuranceMap[id] || null;
  };
  
  // Extract insurance information
  const primaryInsurance = appointment.insurances && appointment.insurances.length > 0 
    ? appointment.insurances[0] 
    : null;
    
  // Get friendly insurance provider name
  const insuranceProviderName = primaryInsurance ? 
    getInsuranceProviderName(primaryInsurance.insurancepackageid) || 
    primaryInsurance.ircname || 
    primaryInsurance.insurancepayername || 
    'Not Available' : 
    'None';

  // Extract patient data
  const patient = appointment.patient || {};
  
  // Get appointment notes
  const appointmentNotes = appointment.appointmentnotes && appointment.appointmentnotes.length > 0
    ? appointment.appointmentnotes.map(note => note.text || note).join(', ')
    : appointment.notes || 'No notes';

  // Get appointment status using comprehensive Athena status code mapping
  const getAppointmentStatus = () => {
    let status = "Booked";
    
    if (appointment.appointmentstatus) {
      // Handle athenaNet status codes
      switch(appointment.appointmentstatus) {
        case 'x':
          // Check if the cancelreasonname is PATIENT NO SHOW
          if (appointment.cancelreasonname === 'PATIENT NO SHOW') {
            status = "No Show";
          } else {
            status = "Cancelled";
          }
          break;
        case 'f': 
          status = "Scheduled";
          break;
        case 'o':
          status = "Open";
          break;
        case '2':
          status = "Checked In";
          break;
        case '3':
          status = "Checked Out";
          break;
        case '4':
          status = "Charge Entered";
          break;
        default:
          // Handle string status descriptions for backward compatibility
          if (appointment.appointmentstatus === 'cancelled') status = "Cancelled";
          else if (appointment.appointmentstatus === 'completed') status = "Completed";
          else if (appointment.appointmentstatus === 'checked-in') status = "Checked In";
          else status = "Booked";
      }
    } else if (appointment.status) {
      // Use existing status if appointmentstatus is not available
      status = appointment.status;
    } else if (appointment.cancelled) {
      status = "Cancelled";
    } else if (appointment.completed) {
      status = "Completed";
    } else if (appointment.checkedin) {
      status = "Checked In";
    }
    
    return status;
  };

  // Get appointment date and time
  const getFormattedDateTime = () => {
    let dateString = "";
    let timeString = "";
    
    if (appointment.date) {
      if (typeof appointment.date === 'string') {
        dateString = formatDate(appointment.date);
      } else {
        dateString = formatDate(new Date(appointment.date).toLocaleDateString());
      }
    } else if (appointment.appointmentdate) {
      dateString = formatDate(appointment.appointmentdate);
    }
    
    if (appointment.starttime) {
      timeString = appointment.starttime;
    } else if (appointment.appointmenttime) {
      timeString = appointment.appointmenttime;
    } else if (appointment.time) {
      timeString = appointment.time;
    }
    
    return { date: dateString, time: timeString };
  };

  const { date: appointmentDate, time: appointmentTime } = getFormattedDateTime();
  const appointmentStatus = getAppointmentStatus();

  // Handle No-Show functionality
  const handleNoShow = async () => {
    try {
      // Use the fixed PATIENT NO SHOW reason ID (9)
      const noShowReasonId = 9;
      
      const response = await athenaService.cancelAppointment({
        appointmentId: appointment.appointmentid || appointment.id,
        patientId: appointment.patientid || appointment.patient?.patientid || appointment.patient?.id,
        appointmentCancelReasonId: noShowReasonId,
        cancellationReason: 'PATIENT NO SHOW',
        ignoreSchedulablePermission: true
      });
      
      console.log('Appointment marked as no-show:', response);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Appointment marked as no-show successfully',
        severity: 'success'
      });
      
      // Close the details view after a brief delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error marking appointment as no-show:', error);
      setSnackbar({
        open: true,
        message: `Failed to mark as no-show: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Handle appointment checkout
  const handleCheckout = async () => {
    if (!appointment || !appointment.appointmentid) {
      setSnackbar({
        open: true,
        message: 'Cannot checkout: Invalid appointment ID',
        severity: 'error'
      });
      return;
    }

    setIsCheckingOut(true);
    try {
      const response = await athenaService.checkoutAppointment(appointment.appointmentid);
      
      if (response && response.success) {
        setSnackbar({
          open: true,
          message: 'Appointment checked out successfully',
          severity: 'success'
        });
        
        // If we have an onClose callback, call it to refresh the appointments list
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } else {
        setSnackbar({
          open: true,
          message: `Checkout failed: ${response?.errormessage || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error checking out appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not complete appointment checkout';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Checkout failed: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Handle appointment check-in
  const handleCheckin = async () => {
    if (!appointment || !appointment.appointmentid) {
      setSnackbar({
        open: true,
        message: 'Cannot check in: Invalid appointment ID',
        severity: 'error'
      });
      return;
    }

    setIsCheckingIn(true);
    try {
      const response = await athenaService.checkinAppointment(appointment.appointmentid);
      
      if (response && response.success) {
        setSnackbar({
          open: true,
          message: 'Patient checked in successfully',
          severity: 'success'
        });
        
        // If we have an onClose callback, call it to refresh the appointments list
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 1500);
        }
      } else {
        setSnackbar({
          open: true,
          message: `Check-in failed: ${response?.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error checking in appointment:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not complete check-in';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      setSnackbar({
        open: true,
        message: `Check-in failed: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        borderBottom: '1px solid #eaeaea',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onClose} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={500}>
            Appointment Details
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip 
            label={appointmentStatus} 
            size="small"
            color={
              appointmentStatus === "Cancelled" ? "error" :
              appointmentStatus === "Completed" ? "success" :
              appointmentStatus === "Checked In" ? "info" : "primary"
            }
            sx={{ mr: 2 }}
          />
          <IconButton sx={{ mr: 1 }}>
            <PrintIcon />
          </IconButton>
          <IconButton>
            <ChatIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="success"
            size="small"
            sx={{ mr: 1 }}
            disabled={isCheckingIn}
            startIcon={isCheckingIn ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            onClick={handleCheckin}
          >
            {isCheckingIn ? 'Processing...' : 'Check In'}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={isCheckingOut}
            startIcon={isCheckingOut ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            onClick={handleCheckout}
          >
            {isCheckingOut ? 'Processing...' : 'Check Out'}
          </Button>
        </Box>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', mb: 3 }}>
        <Button 
          startIcon={<ScheduleIcon />} 
          variant="outlined" 
          size="small"
          sx={{ mr: 2, borderRadius: 4 }}
          onClick={handleOpenRescheduleDialog}
        >
          Reschedule
        </Button>
        <Button 
          startIcon={<CancelIcon />} 
          variant="outlined" 
          size="small"
          sx={{ mr: 2, borderRadius: 4 }}
          onClick={handleOpenCancelDialog}
        >
          Cancel
        </Button>
        <Button 
          startIcon={<NoShowIcon />} 
          variant="outlined" 
          size="small"
          sx={{ borderRadius: 4 }}
          onClick={handleNoShow}
        >
          Mark as No-Show
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="appointment details tabs">
          <StyledTab label="Details" />
          <StyledTab label="Forms" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Left Side - Appointment Details */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eaeaea', borderRadius: 2, mb: 3 }}>
              <SectionTitle>
                Appointment Details
              </SectionTitle>
              
              <DetailContainer>
                <DetailLabel>Appointment Time</DetailLabel>
                <DetailValue>
                  {appointmentDate} at {appointmentTime || '2:00 pm'}
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Appointment Duration</DetailLabel>
                <DetailValue>{appointment.duration || '40'} minutes</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Calendar Name</DetailLabel>
                <DetailValue>
                  {(() => {
                    console.log("Provider debugging:", {
                      appointmentProviderID: appointment.providerid,
                      availableProviders: providers.map(p => `${p.providerid}: ${p.lastname}`),
                      appointment: appointment
                    });
                    
                    // Use numerical comparison for provider ID
                    const provider = providers.find(p => Number(p.providerid) === Number(appointment.providerid));
                    
                    // Special case for IV Office (provider ID 30)
                    if (provider && Number(provider.providerid) === 30) {
                      return "IV Office";
                    }
                    
                    if (provider) {
                      if (provider.firstname || provider.lastname) {
                        return `${provider.firstname || ''} ${provider.lastname || ''}, ${provider.providertypeid || ''}`;
                      } else {
                        return provider.displayname || appointment.providername || '';
                      }
                    }
                    return appointment.providername || '';
                  })()} / 
                  {(() => {
                    const department = departments.find(d => d.departmentid.toString() === appointment.departmentid?.toString());
                    let locationName = department?.city || appointment.departmentname || '';
                    
                    // Format location name in camelcase
                    if (locationName) {
                      locationName = locationName.toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    }
                    
                    return locationName;
                  })()}
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Provider</DetailLabel> 
                <DetailValue>
                  {(() => {
                    // Use numerical comparison for provider ID
                    const provider = providers.find(p => Number(p.providerid) === Number(appointment.providerid));
                    
                    // Special case for IV Office (provider ID 30)
                    if (provider && Number(provider.providerid) === 30) {
                      return "IV Office";
                    }
                    
                    if (provider) {
                      if (provider.firstname || provider.lastname) {
                        return `${provider.firstname || ''} ${provider.lastname || ''}, ${provider.providertypeid || ''}`.trim();
                      } else {
                        return provider.displayname || appointment.providername || '';
                      }
                    }
                    return appointment.providername || '';
                  })()} 
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Location</DetailLabel>
                <DetailValue>
                  {(() => {
                    const department = departments.find(d => d.departmentid.toString() === appointment.departmentid?.toString());
                    let locationName = department?.city || appointment.departmentname || '';
                    
                    // Format location name in camelcase
                    if (locationName) {
                      locationName = locationName.toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                    }
                    
                    return locationName;
                  })()}
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Requested Time</DetailLabel>
                <DetailValue>
                  Requested {formatDate(appointment.lastmodified?.split(' ')[0])} at {appointment.lastmodified?.split(' ')[1] || '5:37 pm'}
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Source</DetailLabel>
                <DetailValue>{appointment.source || 'PatientPop'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Online Booking Reason</DetailLabel>
                <DetailValue>{appointment.patientappointmenttypename || 'New Patient'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Patient comments</DetailLabel>
                <DetailValue>{appointmentNotes || 'No comments'}</DetailValue>
              </DetailContainer>
            </Paper>
            
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eaeaea', borderRadius: 2 }}>
              <SectionTitle>
                Reminder Communication
              </SectionTitle>
              
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mb: 2 }}>
                <NoShowIcon sx={{ mr: 1 }} />
                <Typography variant="body2">No Reminder Sent</Typography>
              </Box>
              
              <Button 
                variant="outlined" 
                size="small" 
                sx={{ 
                  borderRadius: 4, 
                  border: '1px solid #e0e0e0',
                  color: 'text.primary',
                  fontSize: '0.75rem'
                }}
              >
                Patient Confirmed
              </Button>
            </Paper>
          </Grid>
          
          {/* Right Side - Patient Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eaeaea', borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <SectionTitle>
                  Patient Information
                </SectionTitle>
                
                {patient.newpatient && (
                  <Chip 
                    label="NEW PATIENT" 
                    size="small"
                    sx={{ 
                      bgcolor: '#e3f2fd', 
                      color: '#2196f3',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      height: 24
                    }} 
                  />
                )}
              </Box>
              
              <DetailContainer>
                <DetailLabel>First Name</DetailLabel>
                <DetailValue>{patient.firstname || 'George'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Last Name</DetailLabel>
                <DetailValue>{patient.lastname || 'Grigoryan'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Phone Number</DetailLabel>
                <DetailValue>{patient.mobilephone || patient.homephone || '(669) 272-4905'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Email Address</DetailLabel>
                <DetailValue>{patient.email || 'gevorg.grigoryan99@gmail.com'}</DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Date Of Birth</DetailLabel>
                <DetailValue>
                  {formatDate(patient.dob)} {calculateAge(patient.dob)}
                </DetailValue>
              </DetailContainer>
              
              <DetailContainer>
                <DetailLabel>Sex</DetailLabel>
                <DetailValue>
                  {patient.sex === 'M' ? 'Male' : 
                   patient.sex === 'F' ? 'Female' : 
                   patient.sex === 'O' ? 'Other' : 'Undisclosed'}
                </DetailValue>
              </DetailContainer>
            </Paper>
            
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eaeaea', borderRadius: 2 }}>
              <SectionTitle>
                Insurance Information
              </SectionTitle>
              
              {primaryInsurance ? (
                <>
                  <DetailContainer>
                    <DetailLabel>Provider</DetailLabel>
                    <DetailValue>{insuranceProviderName}</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Member ID</DetailLabel>
                    <DetailValue>{primaryInsurance.insuranceidnumber || primaryInsurance.policynumber || 'Not Available'}</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Group Number</DetailLabel>
                    <DetailValue>{primaryInsurance.insurancepackageid || 'Not Available'}</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Phone Number</DetailLabel>
                    <DetailValue>{primaryInsurance.insurancephone || 'Not Available'}</DetailValue>
                  </DetailContainer>
                </>
              ) : (
                <>
                  <DetailContainer>
                    <DetailLabel>Provider</DetailLabel>
                    <DetailValue>Not Available</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Member ID</DetailLabel>
                    <DetailValue>Not Available</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Group Number</DetailLabel>
                    <DetailValue>Not Available</DetailValue>
                  </DetailContainer>
                  
                  <DetailContainer>
                    <DetailLabel>Phone Number</DetailLabel>
                    <DetailValue>Not Available</DetailValue>
                  </DetailContainer>
                </>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <SectionTitle>
                Notification Opt Outs
              </SectionTitle>
              
              <DetailValue>None</DetailValue>
            </Paper>
          </Grid>
          
          {/* Notes Section */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eaeaea', borderRadius: 2 }}>
              <SectionTitle>
                Notes
              </SectionTitle>
              
              <DetailValue>{appointmentNotes || 'No notes'}</DetailValue>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {tabValue === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1">Forms content will go here</Typography>
        </Box>
      )}
      
      {/* Cancellation Reason Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={handleCloseCancelDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          {loadingReasons ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Please select a reason for cancelling this appointment:
              </Typography>
              
              {cancelReasons && cancelReasons.length > 0 ? (
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <FormLabel component="legend">Cancellation Reason</FormLabel>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    {cancelReasons.length} reason(s) available
                  </Typography>
                  <RadioGroup
                    value={selectedCancelReasonId}
                    onChange={handleCancelReasonChange}
                  >
                    {cancelReasons.map((reason) => {
                      console.log('Rendering reason:', reason);
                      return (
                        <FormControlLabel 
                          key={reason.appointmentcancelreasonid} 
                          value={reason.appointmentcancelreasonid.toString()} 
                          control={<Radio />} 
                          label={reason.name} 
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              ) : (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  No cancellation reasons available. Please enter a custom reason.
                </Typography>
              )}
              
              <TextField
                label="Additional Comments (Optional)"
                multiline
                rows={3}
                fullWidth
                value={customCancelReason}
                onChange={handleCustomReasonChange}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={submittingCancel}>Cancel</Button>
          <Button 
            onClick={handleCancelAppointment} 
            color="primary" 
            variant="contained"
            disabled={((!selectedCancelReasonId && cancelReasons.length > 0) || submittingCancel)}
            startIcon={submittingCancel ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submittingCancel ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reschedule Dialog */}
      <Dialog 
        open={rescheduleDialogOpen} 
        onClose={handleCloseRescheduleDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Step 1: Select Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 1: Select Location and Provider
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingDepartments}>
                <InputLabel id="location-label">Location</InputLabel>
                <Select
                  labelId="location-label"
                  id="location-select"
                  value={selectedLocation}
                  label="Location"
                  onChange={handleLocationChange}
                >
                  <MenuItem value="">
                    <em>Select location</em>
                  </MenuItem>
                  {Object.keys(departmentsByLocation).map((locationName) => (
                    <MenuItem key={locationName} value={locationName}>
                      {locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Hidden department select - populated automatically based on location */}
              <input 
                type="hidden" 
                value={selectedDepartment} 
                onChange={() => {}}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }} disabled={loadingProviders || !selectedDepartment || (appointment && appointment.providerid && selectedProvider === appointment.providerid.toString())}>
                <InputLabel id="provider-label">Provider {appointment && appointment.providerid && selectedProvider === appointment.providerid.toString() ? '(Auto-filled)' : '(Optional)'}</InputLabel>
                <Select
                  labelId="provider-label"
                  id="provider-select"
                  value={selectedProvider}
                  label={`Provider ${appointment && appointment.providerid && selectedProvider === appointment.providerid.toString() ? '(Auto-filled)' : '(Optional)'}`}
                  onChange={handleProviderChange}
                  inputProps={{
                    readOnly: appointment && appointment.providerid && selectedProvider === appointment.providerid.toString()
                  }}
                >
                  <MenuItem value="">
                    <em>All Providers</em>
                  </MenuItem>
                  {providers.map((provider) => (
                    <MenuItem key={provider.providerid || provider.id} value={provider.providerid || provider.id}>
                      {provider.firstname ? `${provider.firstname} ${provider.lastname}` : provider.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Step 2: Select Date */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 2: Select Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Appointment Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  slotProps={{ textField: { fullWidth: true } }}
                  sx={{ mb: 2 }}
                />
              </LocalizationProvider>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={fetchAvailableSlots}
                  disabled={!selectedDepartment || !isValid(selectedDate)}
                >
                  Find Available Slots
                </Button>
              </Box>
            </Grid>
            
            {/* Step 3: Select Time Slot */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 3: Select Available Time
              </Typography>
              
              {loadingSlots ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {availableSlots.length > 0 ? (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel id="slot-select-label">Available Appointment Slots</InputLabel>
                      <Select
                        labelId="slot-select-label"
                        id="slot-select"
                        value={selectedSlotId}
                        label="Available Appointment Slots"
                        onChange={handleSlotChange}
                      >
                        {availableSlots.map((slot) => (
                          <MenuItem 
                            key={slot.appointmentid || slot.id} 
                            value={slot.appointmentid || slot.id}
                          >
                            {slot.date || slot.appointmentdate}, {slot.starttime || slot.appointmenttime} - {slot.duration ? `${slot.duration} min` : 'Duration not specified'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ mb: 3 }}>
                      {availableSlots.length === 0 && selectedDepartment ? 
                        'No available appointment slots found for the selected date. Please try another date.' :
                        'Search for available slots by selecting a location and date.'}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
            
            {/* Step 4: Reason for Rescheduling */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
                Step 4: Reason for Rescheduling
              </Typography>
              
              {loadingReasons ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading cancellation reasons...
                  </Typography>
                </Box>
              ) : cancelReasons.length > 0 ? (
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component="legend">Reschedule Reason</FormLabel>
                  <RadioGroup
                    value={selectedCancelReasonId}
                    onChange={handleCancelReasonChange}
                  >
                    {cancelReasons.map((reason) => (
                      <FormControlLabel 
                        key={reason.appointmentcancelreasonid} 
                        value={reason.appointmentcancelreasonid.toString()} 
                        control={<Radio />} 
                        label={reason.name} 
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  No cancellation reasons available. Please try refreshing the dialog.
                </Alert>
              )}
              
              <TextField
                label="Additional Comments (Optional)"
                multiline
                rows={3}
                fullWidth
                value={rescheduleReason}
                onChange={handleRescheduleReasonChange}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRescheduleDialog} disabled={submittingReschedule}>Cancel</Button>
          <Button 
            onClick={handleRescheduleAppointment} 
            color="primary" 
            variant="contained"
            disabled={!selectedSlotId || (!selectedCancelReasonId && cancelReasons.length > 0) || submittingReschedule}
            startIcon={submittingReschedule ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submittingReschedule ? 'Processing...' : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppointmentDetails;
